import { useEffect } from 'react'

import { useUnit } from 'effector-react'

import { Page } from '@/components/layout/page/page'
import { usePaginationState } from '@/components/ui/table/hooks/usePaginationState'
import { useSearchQuery } from '@/components/ui/table/hooks/useSearchQuery'
import { DIALOG_TYPE } from '@/dialogs/constants'
import { $creatorId } from '@/models/creators'
import { openDialog } from '@/models/dialogs'
import { CustomListsHeaderButtons } from '@/modules/creator-settings/lists/custom-lists/components/CustomListsHeaderButtons'
import { CustomListsTable } from '@/modules/creator-settings/lists/custom-lists/components/CustomListsTable'
import { useCustomListMutations } from '@/modules/creator-settings/lists/custom-lists/hooks/useCustomListMutations'
import { useCustomLists } from '@/modules/creator-settings/lists/custom-lists/hooks/useCustomLists'
import { TrackedFeatures, trackFeatureUsage } from '@/utils/feature-engagement'

const MAX_CUSTOM_LISTS_COUNT = 10

export const CustomListsView = () => {
  const creatorId = useUnit($creatorId)
  const { searchQuery, setSearchQuery } = useSearchQuery('')
  const { paginationState, setPaginationState } = usePaginationState({
    pageIndex: 0,
    pageSize: 10,
  })

  const { createCustomListMutation } = useCustomListMutations()
  const { customLists, isLoading } = useCustomLists(
    paginationState,
    searchQuery,
  )

  useEffect(() => {
    if (customLists?.content?.length) {
      trackFeatureUsage(TrackedFeatures.CustomLists, creatorId)
    }
  }, [customLists?.content?.length, creatorId])

  const handleCreateListPress = () => {
    openDialog({
      type: DIALOG_TYPE.CUSTOM_LIST_FORM,
      title: 'Create new Custom List',
      testId: 'create-custom-list-dialog',
      submitLabel: 'Create',
      onSubmit: async (values) => {
        return createCustomListMutation.mutateAsync(values)
      },
    })
  }

  return (
    <Page
      title="Custom Lists"
      description="You can create up to 10 custom lists to segment your fans. To add a new list, you’ll need to delete an existing one."
      headerOptions={
        <CustomListsHeaderButtons
          setFilter={setSearchQuery}
          onCreateListPress={handleCreateListPress}
          disabled={
            (customLists?.content?.length ?? 0) >= MAX_CUSTOM_LISTS_COUNT
          }
        />
      }
    >
      <CustomListsTable
        data={customLists?.content ?? []}
        isFetching={isLoading}
        paginationState={paginationState}
        onPaginationChange={setPaginationState}
        pageCount={customLists?.totalPages ?? 0}
      />
    </Page>
  )
}
